/**
 * "O que fazer agora" da LISTA de leads: ate quatro cartoes acionaveis lidos
 * do RESUMO do periodo (`/api/leads/summary`), nunca da pagina carregada.
 *
 * Motivo: a pagina chega filtrada e paginada; um aviso sobre "alto valor
 * parado" nao pode sumir porque a pessoa filtrou por "recuperados". O
 * resumo agrega o periodo inteiro no servidor (no mock, sobre o pool) e
 * continua valendo com milhares de leads.
 *
 * Cada acao recorta a propria lista (aplica filtros) ou leva a uma tela que
 * resolve o problema (integracoes, funil). Nada aqui propoe falar com quem
 * ainda nao saiu: todo insight parte de um abandono ou vencimento ja
 * ocorrido e do que a regua fez com essa pessoa desde entao.
 *
 * Ordem = urgencia: dinheiro parado com janela de credito correndo vem antes
 * de comparacao com o periodo anterior, que vem antes de "nada a fazer".
 */

import type { IconName } from '../../components/ui';
import { formatBRLFromCents, formatCount, formatDateTime } from '../../lib/format';
import { JANELA_DE_CREDITO_DIAS } from './resumo';
import type { ConsultaDeLeads, ResumoDeLeads } from './types';
import { duracaoCurta } from './vocabulario';

export type TomDoInsight = 'alerta' | 'oportunidade' | 'projecao' | 'bom';

export type AcaoDoInsight = { readonly kind: 'filtro'; readonly consulta: Partial<ConsultaDeLeads> } | { readonly kind: 'rota'; readonly to: string };

export interface InsightDaLista {
  readonly key: string;
  readonly tom: TomDoInsight;
  readonly icone: IconName;
  readonly titulo: string;
  readonly texto: string;
  readonly acao: AcaoDoInsight | null;
  readonly rotuloAcao: string | null;
}

const DIA_MS = 86_400_000;

export function gerarInsightsDaLista(resumo: ResumoDeLeads, agora: Date): readonly InsightDaLista[] {
  const lista: InsightDaLista[] = [];

  // 1. Alto valor sem voltar: dinheiro grande, talvez ainda com janela de credito.
  if (resumo.highValueExhausted > 0) {
    const n = resumo.highValueExhausted;
    lista.push({
      key: 'alto-valor-parado',
      tom: 'oportunidade',
      icone: 'estrela',
      titulo: `${formatCount(n)} ${n === 1 ? 'carrinho de alto valor esgotou' : 'carrinhos de alto valor esgotaram'} a régua sem voltar · ${formatBRLFromCents(resumo.highValueExhaustedAmountCents)}`,
      texto: `Alto valor = a partir de ${formatBRLFromCents(resumo.highValueThresholdCents)} (percentil 80 dos carrinhos da empresa). Quem ainda está dentro dos ${JANELA_DE_CREDITO_DIAS} dias após o último contato pode ser recuperado por uma mensagem manual ou uma ligação da vendedora — e a venda ainda credita comissão. Abra o lead para ver até quando.`,
      acao: { kind: 'filtro', consulta: { altoValor: true, situacao: 'esgotado', sort: 'amount', dir: 'desc' } },
      rotuloAcao: 'Ver esses leads',
    });
  }

  // 2. Com o comercial ha mais de um dia: a janela de credito esta correndo.
  if (resumo.withSellerOver1d > 0) {
    const n = resumo.withSellerOver1d;
    const vence = resumo.oldestSellerContactAt === null ? null : new Date(new Date(resumo.oldestSellerContactAt).getTime() + JANELA_DE_CREDITO_DIAS * DIA_MS);
    lista.push({
      key: 'com-comercial-parado',
      tom: 'alerta',
      icone: 'usuarios',
      titulo: `${formatCount(n)} ${n === 1 ? 'lead está' : 'leads estão'} com a vendedora há mais de 1 dia · ${formatBRLFromCents(resumo.withSellerOver1dAmountCents)}`,
      texto:
        vence === null
          ? 'Responderam à régua e a vendedora assumiu. O crédito da venda só vale até 7 dias depois desse contato.'
          : vence.getTime() > agora.getTime()
            ? `Responderam à régua e a vendedora assumiu. O crédito mais antigo vence em ${duracaoCurta((vence.getTime() - agora.getTime()) / 60_000)} (${formatDateTime(vence.toISOString())}); depois disso a venda não gera comissão.`
            : `Responderam à régua e a vendedora assumiu, mas o crédito mais antigo já venceu em ${formatDateTime(vence.toISOString())}. Feche ou libere esses leads.`,
      acao: { kind: 'filtro', consulta: { situacao: 'com_comercial', sort: 'abandonedAt', dir: 'asc' } },
      rotuloAcao: 'Ver quem está com o comercial',
    });
  }

  // 3. Falha do motor: canal fora do ar derruba todo mundo do mesmo tier.
  if (resumo.failed > 0) {
    lista.push({
      key: 'falhas-do-motor',
      tom: 'alerta',
      icone: 'alerta',
      titulo: `${formatCount(resumo.failed)} ${resumo.failed === 1 ? 'régua encerrada' : 'réguas encerradas'} por falha de canal`,
      texto: 'O motor desistiu porque o WhatsApp (ou a voz) não respondeu. Enquanto a integração estiver fora, todo lead que chegar ao mesmo toque para no mesmo lugar. Confira as integrações.',
      acao: { kind: 'rota', to: '/configuracoes' },
      rotuloAcao: 'Ver integrações',
    });
  }

  // 4. Oferta com muitos abandonos e nenhuma recuperacao.
  if (resumo.offerWithoutRecovery !== null) {
    const o = resumo.offerWithoutRecovery;
    lista.push({
      key: `oferta-sem-recuperacao-${o.offerId}`,
      tom: 'alerta',
      icone: 'grafico',
      titulo: `Nenhum dos ${formatCount(o.abandoned)} abandonos de “${o.name}” foi recuperado`,
      texto: 'Quando uma oferta não converte em nenhum toque, o problema costuma ser o checkout (preço, parcelamento, forma de pagamento), não a régua. Veja o que essas pessoas responderam.',
      acao: { kind: 'filtro', consulta: { offerId: o.offerId, situacao: null, altoValor: false } },
      rotuloAcao: 'Ver leads desta oferta',
    });
  }

  // 5. Comparacao com o periodo anterior (recuperados).
  if (resumo.previous !== null && resumo.previous.recovered > 0) {
    const diff = resumo.recovered - resumo.previous.recovered;
    if (diff < 0 && Math.abs(diff) * 10 >= resumo.previous.recovered) {
      lista.push({
        key: 'recuperados-cairam',
        tom: 'alerta',
        icone: 'grafico',
        titulo: `Recuperados caíram de ${formatCount(resumo.previous.recovered)} para ${formatCount(resumo.recovered)} vs. período anterior`,
        texto: `A receita recuperada foi de ${formatBRLFromCents(resumo.previous.recoveredAmountCents)} para ${formatBRLFromCents(resumo.recoveredAmountCents)}. Compare a taxa por oferta no funil antes de mexer na régua.`,
        acao: { kind: 'rota', to: '/funis/carrinho' },
        rotuloAcao: 'Abrir o funil',
      });
    } else if (diff > 0 && diff * 5 >= resumo.previous.recovered) {
      lista.push({
        key: 'recuperados-subiram',
        tom: 'bom',
        icone: 'atualizar',
        titulo: `Recuperados subiram de ${formatCount(resumo.previous.recovered)} para ${formatCount(resumo.recovered)} vs. período anterior`,
        texto: `Receita recuperada de ${formatBRLFromCents(resumo.recoveredAmountCents)} (comissão ${formatBRLFromCents(resumo.commissionCents)}). Nada a mudar: a régua está funcionando.`,
        acao: { kind: 'filtro', consulta: { situacao: 'recuperado', altoValor: false } },
        rotuloAcao: 'Ver recuperados',
      });
    }
  }

  // 6. Projecao curta: quem entra no primeiro toque na proxima meia hora.
  if (resumo.dueSoon > 0 && lista.length < 4) {
    const n = resumo.dueSoon;
    lista.push({
      key: 'primeiro-toque-em-breve',
      tom: 'projecao',
      icone: 'relogio',
      titulo: `${formatCount(n)} ${n === 1 ? 'lead recebe' : 'leads recebem'} o primeiro toque nos próximos 30 min · ${formatBRLFromCents(resumo.dueSoonAmountCents)}`,
      texto: 'Nada a fazer: a espera é de propósito. Quem pagar sozinho até lá sai da régua sem custo; quem não pagar recebe a ligação da IA.',
      acao: { kind: 'filtro', consulta: { situacao: 'na_fila', altoValor: false } },
      rotuloAcao: 'Ver a fila',
    });
  }

  if (lista.length === 0) {
    lista.push({
      key: 'nada-urgente',
      tom: 'bom',
      icone: 'check',
      titulo: 'Nada urgente por aqui',
      texto: 'Nenhum carrinho de alto valor parado, ninguém esperando o comercial há mais de um dia e nenhuma falha de canal. Um aviso aparece quando dinheiro fica parado com a janela de crédito correndo.',
      acao: null,
      rotuloAcao: null,
    });
  }

  return lista.slice(0, 4);
}
